"use client"

import { useState, useEffect, useReducer, useCallback, useRef } from "react"
import { usePathname } from "next/navigation"

import type { BannerData } from "./banner.data"
import { navigation } from "./navigation.data"

import NavigationLogo from "./NavigationLogo"
import Link from "next/link"
import NavigationToggle from "./NavigationToggle"
import NavigationBar from "./NavigationBar"
import { ChevronRightIconLarge } from "../icons/ChevronRightIcon"
import { useCommandK } from "@/helpers/CommandK"
import SearchIcon from "../icons/SearchIcon"
import { useContentVisibility } from "@/helpers/Providers"
import CmdKItem from "../icons/CmdKItem"
import { useLoggedIn } from "@/helpers/isLoggedIn"
import clsx from "clsx"

// Define the state type
type NavigationState = {
  isOpen: boolean;
  submenuOpen: boolean;
}

// Define action types
type NavigationAction =
  | { type: 'TOGGLE_MENU' }
  | { type: 'CLOSE_MENU' }
  | { type: 'TOGGLE_SUBMENU' }
  | { type: 'SET_SUBMENU'; value: boolean }
  | { type: 'RESET' };

// Navigation reducer function
function navigationReducer(state: NavigationState, action: NavigationAction): NavigationState {
  switch (action.type) {
    case 'TOGGLE_MENU':
      return { ...state, isOpen: !state.isOpen };
    case 'CLOSE_MENU':
      return { ...state, isOpen: false };
    case 'TOGGLE_SUBMENU':
      return { ...state, submenuOpen: !state.submenuOpen };
    case 'SET_SUBMENU':
      return { ...state, submenuOpen: action.value };
    case 'RESET':
      return { isOpen: false, submenuOpen: false };
    default:
      return state;
  }
}

export default function Navigation({
  banner,
}: { banner: BannerData }) {
  const { show } = useCommandK()
  const { hide } = useContentVisibility()

  let pathname = usePathname()
  if (pathname.includes("blog")) pathname = "/blog"

  const isLandingPage = pathname.startsWith("/lp/")

  // Replace multiple useState calls with a single useReducer
  const [navState, dispatch] = useReducer(navigationReducer, {
    isOpen: false,
    submenuOpen: false
  });

  const { isOpen, submenuOpen } = navState;
  const navBackHandlerRef = useRef<(() => boolean) | null>(null);

  // Create memoized handler functions
  const toggleMenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_MENU' });
  }, []);

  const toggleSubmenu = useCallback(() => {
    dispatch({ type: 'TOGGLE_SUBMENU' });
  }, []);

  const setSubmenuOpen = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SUBMENU', value });
  }, []);

  const setIsOpen = useCallback((value: boolean) => {
    if (!value) {
      dispatch({ type: 'CLOSE_MENU' });
    } else {
      dispatch({ type: 'TOGGLE_MENU' });
    }
  }, []);

  const loggedIn = useLoggedIn();

  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [pathname])

  useEffect(() => {
    if (isOpen) document.body.classList.add("overflow-hidden")
    else document.body.classList.remove("overflow-hidden")

    const keyDownHandler = (event: any) => {
      if (event.key === "Escape") dispatch({ type: 'CLOSE_MENU' });
    }

    window.addEventListener("keydown", keyDownHandler)
    return () => window.removeEventListener("keydown", keyDownHandler)
  }, [isOpen])

  //on the signup pages hide the buttons for signing up to avoid confusion
  const showCTAButtons = !pathname.endsWith("sign-up")
  return (
    <div
      data-signup-placement="nav"
      className={clsx(
        "fixed right-0 left-0 p-2 pb-0 z-99",
        (banner && banner.isEnabled ? "top-10" : "top-0")
      )}
    >
      <div className={clsx("bg-grey-900 relative rounded-lg xl:grid xl:grid-cols-[auto_1fr_auto] flex justify-between items-center p-2",
        isLandingPage && "lg:!grid-cols-3"
      )}>
        <div
          onClick={() => hide("/")}
          className={clsx(
            "flex transition-all xl:transition-none relative duration-300 shrink-0",
            (submenuOpen
              ? "-left-16 xl:left-0 ease-in"
              : "left-0 xl:left-0 ease-out")
          )}
        >
          <NavigationLogo
            className={clsx(
              "p-3",
              (submenuOpen
                ? "opacity-0 pointer-events-none xl:pointer-events-auto -left-16 xl:opacity-100"
                : "opacity-100 pointer-events-auto left-0")
            )}
          />
        </div>
        <div
          onClick={() => { if (navBackHandlerRef.current?.()) return; setSubmenuOpen(!submenuOpen); }}
          className={clsx(
            "rotate-180 transition-all xl:transition-none duration-300 cursor-pointer absolute top-1/2 -translate-y-1/2 left-4 z-20 xl:hidden",
            (!submenuOpen
              ? "opacity-0 pointer-events-none xl:pointer-events-auto ml-16 ease-out"
              : "opacity-100 pointer-events-auto ml-0 translate-x-0 ease-in")
          )}
        >
          <ChevronRightIconLarge />
        </div>
        {navigation && !isLandingPage ? (
          <>
            <div
              className={clsx(
                "xl:static fixed p-2 xl:p-0 xl:pl-8 xl:overflow-auto hide-scrollbar inset-0 xl:block",
                (isOpen
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 xl:opacity-100 pointer-events-none xl:pointer-events-auto"),
                (banner?.isEnabled ? "pt-[120px]" : "pt-[76px]")
              )}
            >
              <NavigationBar
                menu={navigation}
                setIsOpen={setIsOpen}
                submenuOpen={submenuOpen}
                setSubmenuOpen={setSubmenuOpen}
                navBackHandlerRef={navBackHandlerRef}
                currentlyOpen={pathname}
                banner={banner?.isEnabled ?? false}
                showCTAButtons={showCTAButtons}
                isLoggedIn={loggedIn}
              />
            </div>
            <div
              className={clsx(
                "fixed -z-1 inset-0 bg-black/32 backdrop-blur-sm pointer-events-none xl:hidden transition-opacity duration-150",
                (isOpen ? "opacity-100" : "opacity-0")
              )}
            />
          </>
        ): <div />}
        <div className="flex justify-end relative z-10 gap-2 items-center">
          <CmdKButton show={show} className={clsx( isLandingPage && "!hidden")} />
          {showCTAButtons &&
            <div className={clsx("hidden xl:block w-px h-6 bg-grey-800 shrink-0", isLandingPage && "!hidden")} />
          }
          {showCTAButtons &&
          <>
            {!loggedIn &&
              <TextLink href="https://app.dash0.com" className={clsx("xl:flex hidden", isLandingPage && "!hidden")}>
                Sign in
              </TextLink>
            }
            <Button href="/book-a-demo" className="xl:flex hidden">
                Book a demo
            </Button>
            <div className="xl:min-w-[112px] shrink-0">
              {!loggedIn ?
                <NavigationGradientButton href="/sign-up">
                  Start Free Trial
                </NavigationGradientButton>
              :
                <NavigationGradientButton href="https://app.dash0.com">
                  Open Dash0
                </NavigationGradientButton>
              }
            </div>
            </>
          }
          <NavigationToggle
            className="xl:hidden"
            isOpen={isOpen}
            toggleNavigation={() => setIsOpen(!isOpen)}
          />
        </div>
      </div>
    </div>
  )
}

function NavigationGradientButton(props: any) {
  return (
    <GradientButton
      {...props}
      className="xl:flex relative z-1 hidden after:content-[''] after:absolute after:z-10 after:inset-0 after:rounded after:border after:border-white/15"
    />
  )
}

export function GradientButton({ children, className, href, onClick, target, rel }: any) {
  return (
    <Link
      href={href || "/newsletter"}
      onClick={onClick}
      target={target}
      rel={rel}
      className={clsx(
        "relative rounded h-10 items-center text-center px-4 leading-none group font-medium bg-[#FD8C66] hover:bg-[#F8494D] transition-colors duration-1000 shrink-0",
        className
      )}
    >
      <span className="relative z-10 whitespace-nowrap">{children}</span>

      {/* Gradient bubbles */}
      <div className="absolute pointer-events-none inset-0 overflow-hidden rounded">
        <div className="animate-random-bubble gradient-bubble absolute" />
      </div>
    </Link>
  )
}

function CmdKButton({ show, className }: { show: () => void, className?: string }) {
  const pathname = usePathname()
  const [macOS, setMacOS] = useState<boolean | string>("loading")

  useEffect(() => {
    // navigator.platform is deprecated, but is still the most reliable way to detect the OS
    const OSisMac =
      typeof window !== "undefined"
        ? window.navigator.platform.includes("Mac")
        : false

    setMacOS(OSisMac)
  }, [])

  return (
    <button
      onClick={show}
      className={clsx(
        "h-10 group rounded px-1.5 items-center flex max-md:justify-center max-md:aspect-square max-md:gap-2 md:w-[80px] justify-center gap-1 text-grey transition-colors duration-150 hover:text-grey-400 shrink-0 cursor-pointer",
        (pathname.includes("/docs") ? "md:hidden" : "flex"),
        className
      )}
      title="Search"
    >
      {macOS === "loading" ? (
        <div className="flex w-8 items-center gap-0.5 max-md:hidden px-1 h-6 rounded-sm transition-colors duration-100" />
      ) : (
        <div className="flex items-center gap-0.5 max-md:hidden px-1 h-6 rounded-sm transition-colors duration-100">
          {macOS ? (
            <CmdKItem />
          ) : (
            <span className="leading-none text-[17px] shrink-0">CTRL</span>
          )}
          <p className="leading-none text-[17px]">K</p>
        </div>
      )}
      <div className="md:h-5 h-6 aspect-square">
        <SearchIcon />
      </div>
    </button>
  )
}

function Button({ children, className, href, onClick }: any) {
  return (
    <Link
      href={href || "/newsletter"}
      onClick={onClick}
      className={clsx(
          "relative shrink-0 rounded h-10 flex items-center text-center px-4 leading-none group font-medium bg-grey-850 hover:bg-grey-800 transition-colors duration-200",
          className
      )}
    >
      {children}
    </Link>
  )
}

function TextLink({ children, className, href, onClick }: any) {
  return (
    <Link
      href={href || "/newsletter"}
      onClick={onClick}
      className={clsx(
          "relative shrink-0 h-10 flex items-center text-center px-2 leading-none font-medium text-grey hover:text-white transition-colors duration-200",
          className
      )}
    >
      {children}
    </Link>
  )
}
